<nav class="sidebar" role="navigation" aria-label="Conversation history" data-testid="conversation-sidebar-nav-root">
	<!-- New Chat Button -->
	<div class="sidebar-top">
		<button class="new-chat-btn" (click)="onNewChat()" data-testid="conversation-sidebar-btn-new-chat">
			<mat-icon class="new-chat-icon material-icons-outlined icon">sms</mat-icon>
			<span>New chat</span>
		</button>
	</div>

	<!-- Search -->
	<div class="search-row">
		<button class="search-btn" (click)="focusSearch()" data-testid="conversation-sidebar-btn-search">
			<mat-icon>search</mat-icon>
			<span>Search chats</span>
		</button>
	</div>

	<!-- Search Overlay (when active) -->
	@if (isSearching) {
		<div class="search-overlay" data-testid="conversation-sidebar-panel-search">
			<div class="search-input-wrapper">
				<mat-icon class="search-icon">search</mat-icon>
				<input
					#searchInput
					type="text"
					[(ngModel)]="searchQuery"
					(keydown)="onSearchKeyDown($event)"
					placeholder="Search chats..."
					class="search-input"
					autofocus
					data-testid="conversation-sidebar-input-search"
				/>
				<button class="search-clear" (click)="clearSearch()" data-testid="conversation-sidebar-btn-clear-search">
					<mat-icon>close</mat-icon>
				</button>
			</div>
			<ul class="search-results">
				@for (conv of filteredConversations; track conv.id) {
					<li class="search-result-item" (click)="onSelectConversation(conv.id)" [attr.data-testid]="'conversation-sidebar-item-search-result-' + conv.id">
						<mat-icon class="mode-badge" [class.mode-rag]="conv.mode === 'rag'" [class.mode-direct]="conv.mode !== 'rag'">
							{{ conv.mode === 'rag' ? 'menu_book' : 'travel_explore' }}
						</mat-icon>
						<span class="result-title">{{ conv.title }}</span>
					</li>
				}
				@if (filteredConversations.length === 0) {
					<li class="search-empty">No results for "{{ searchQuery }}"</li>
				}
			</ul>
		</div>
	}

	<!-- Main Navigation -->
	@if (!isSearching) {
		<div class="nav-section">
			<!-- Projects Section -->
			<div class="section-header">
				<span class="section-label">Projects</span>
			</div>

			<button class="nav-item" (click)="toggleNewProjectInput()" data-testid="conversation-sidebar-btn-new-project">
				<mat-icon>add</mat-icon>
				<span>New project</span>
			</button>

			<!-- New Project Input -->
			@if (showNewProjectInput) {
				<div class="new-project-input-row" data-testid="conversation-sidebar-panel-new-project">
					<input
						type="text"
						[(ngModel)]="newProjectName"
						(keydown)="onNewProjectKeyDown($event)"
						placeholder="Project name..."
						class="inline-input"
						autofocus
						data-testid="conversation-sidebar-input-project-name"
					/>
					<button class="icon-btn confirm" (click)="submitNewProject()" data-testid="conversation-sidebar-btn-confirm-project">
						<mat-icon>check</mat-icon>
					</button>
					<button class="icon-btn cancel" (click)="toggleNewProjectInput()" data-testid="conversation-sidebar-btn-cancel-project">
						<mat-icon>close</mat-icon>
					</button>
				</div>
			}

			<!-- Project List -->
			@for (project of projects; track project.id) {
				<div
					class="nav-item project-item"
					[class.active]="activeProjectId === project.id"
					(click)="onSelectProject(project.id)"
					(contextmenu)="openProjectContextMenu($event, project)"
					[attr.data-testid]="'conversation-sidebar-btn-project-' + project.id"
					tabindex="0"
					role="button"
					(keydown.enter)="onSelectProject(project.id)"
				>
					<mat-icon>folder_open</mat-icon>
					<span>{{ project.name }}</span>
					<button
						class="project-item-menu-btn"
						(click)="$event.stopPropagation(); openProjectContextMenu($event, project)"
						aria-label="More options"
						data-testid="conversation-sidebar-btn-project-menu"
					>
						<mat-icon>more_horiz</mat-icon>
					</button>
				</div>
			}

			<!-- Project Context Menu -->
			<app-context-menu
				[items]="projectContextMenuItems"
				[x]="projectContextMenuX"
				[y]="projectContextMenuY"
				[visible]="!!projectContextMenuId"
				(itemClicked)="onProjectMenuAction($event)"
				(closed)="projectContextMenuId = null"
			></app-context-menu>

			<!-- Project Rename Dialog -->
			<app-rename-dialog
				[visible]="!!projectRenameId"
				title="Rename project"
				[(value)]="projectRenameValue"
				(submitted)="submitProjectRename($event)"
				(cancelled)="cancelProjectRename()"
			></app-rename-dialog>

			<!-- Your Chats Section -->
			<div class="section-header section-header--chats">
				<span class="section-label">Your chats</span>
			</div>

			<!-- Recent Conversations (ungrouped) -->
			<ul class="chat-list">
				@for (conv of ungroupedConversations; track conv.id) {
					<li
						class="chat-item"
						[class.active]="conv.id === activeConversationId && !activeProjectId"
						[class.pinned]="conv.pinned"
						(click)="onSelectConversation(conv.id)"
						(contextmenu)="openContextMenu($event, conv.id)"
						[attr.data-testid]="'conversation-sidebar-item-chat-' + conv.id"
					>
						@if (conv.pinned) {
							<mat-icon class="pin-icon">push_pin</mat-icon>
						}
						<mat-icon
							class="mode-badge"
							[class.mode-rag]="conv.mode === 'rag'"
							[class.mode-direct]="conv.mode !== 'rag'"
							[attr.aria-label]="conv.mode === 'rag' ? 'TRB Library' : 'Ask Anything'"
						>
							{{ conv.mode === 'rag' ? 'menu_book' : 'travel_explore' }}
						</mat-icon>
						<span class="chat-title">{{ conv.title }}</span>
						<button
							class="chat-item-menu-btn"
							(click)="$event.stopPropagation(); openContextMenu($event, conv.id)"
							aria-label="More options"
							data-testid="conversation-sidebar-btn-chat-menu"
						>
							<mat-icon>more_horiz</mat-icon>
						</button>
					</li>
				}
				@if (ungroupedConversations.length === 0) {
					<li class="empty-state">No conversations yet</li>
				}
			</ul>
		</div>
	}

	<!-- Conversation Context Menu -->
	<app-conversation-context-menu
		[visible]="!!contextMenuConvId"
		[x]="contextMenuX"
		[y]="contextMenuY"
		[projects]="projects"
		[convHasProject]="contextMenuConvHasProject"
		[convProjectId]="contextMenuConvProjectId"
		[convIsPinned]="contextMenuConvIsPinned"
		(closed)="contextMenuConvId = null"
		(rename)="startRenameConversation()"
		(moveToProject)="moveToProject(contextMenuConvId!, $event)"
		(togglePin)="togglePinConversation()"
		(deleteConv)="deleteConversation()"
	></app-conversation-context-menu>

	<!-- Rename Dialog -->
	<app-rename-dialog [visible]="!!renamingConvId" title="Rename chat" [(value)]="renameValue" (submitted)="submitRenameConversation($event)" (cancelled)="cancelConvRename()"></app-rename-dialog>
</nav>
